import React, { useEffect, useState, useMemo } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  CardHeader,
  Row,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table
} from "reactstrap";
import * as moment from "moment";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import ConsultaLista from "../../Components/Common/ConsultaLista";
import { api } from "../../config";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Loader from "../../Components/Common/Loader";
import { fetchComAuth } from "../../common/api";

const ListaLogs = ({}) => {
  const [dados, setDados] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentChanges, setCurrentChanges] = useState(null);
  const [feiraSelecionada, setFeiraSelecionada] = useState('');

  const authUser = JSON.parse(sessionStorage.getItem("authUser")) || {};
  const userData = authUser.user || authUser;
  const userType = userData.tipo || '';
  const userFeirasIds = userData.feiras?.map(item => item.feira_id) || [];

  useEffect(() => {
    const fetchDados = async () => {
      setIsLoading(true);
      try {
        const response = await fetchComAuth(`${api.API_URL}/auditoria`, {
          method: 'GET'
        });

        if (!response.ok) throw new Error("Erro ao buscar os dados");
        const dados = await response.json();
        
        const filtradas = filtrarListaFeiraUsuario(dados.data);
        const dadosFormatados = filtradas.map(item => ({
          ...item,
          // nome_expositor: item.nome_expositor || 'Não informado',
          // area_atuacao: item.area_atuacao || "Não informado"
        }));
        setDados(dadosFormatados);
        setError(null);
      } catch (error) {
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDados();
  }, []);

  const toggleModal = () => setModalOpen(!modalOpen);

  const handleShowDetails = (changes) => {
    setCurrentChanges(changes);
    setModalOpen(true);
  };

  const countChanges = (changes) => {
    if (!changes) return 0;
    return Object.values(changes).filter(v => v.antes !== v.depois).length;
  };

  // const formatValue = (value) => {
  //   if (value === null || value === '' || value === undefined) {
  //     return <span className="text-muted">(vazio)</span>;
  //   }
  //   return value;
  // };

  const formatValue = (value) => {
    if (value === null || value === '' || value === undefined) {
      return <span className="text-muted">(vazio)</span>;
    }
    // Se for um objeto (como o das tags), transforma em string JSON ou texto amigável
    if (typeof value === 'object') {
      return JSON.stringify(value); 
    }
    return value;
  };

  const translateField = (field) => {
    const translations = {
      'nome_fantasia': 'Nome Fantasia',
      'inscricao_estadual': 'Inscrição Estadual',
      'feira_id': 'ID da Feira',
      'cep': 'CEP',
      'pais': 'País',
      'bairro': 'Bairro',
      'endereco': 'Endereço',
      'email_renovacao': 'E-mail de Renovação',
      'manual': 'Manual',
      'tag_id': 'ID da Tag',
      'valor_salvo': 'Valor Unitário',
      'quantidade_salva': 'Quantidade',
      'valor_total_geral': 'Valor Total Geral',
      'area': 'Área (m²)',
      'kva_quantidade_salva': 'Qtd. KVA',
      'estacionamento_quantidade_salva': 'Qtd. Estacionamento'
      // Adicione mais traduções conforme necessário
    };
    return translations[field] || field;
  };

  const renderTagValue = (tagObject) => {
    if (!tagObject || typeof tagObject !== 'object') return formatValue(tagObject);
    
    const { tag_id, valor_salvo, quantidade_salva, manual } = tagObject;
    return `Tag #${tag_id} | ${manual ? 'Manual' : 'Auto'} | Unit: R$ ${valor_salvo || 0} | Qtd: ${quantidade_salva || 1}`;
  };

  // Column
  const columns = useMemo(
    () => [
      {
        header: "Tabela",
        accessorKey: "tabela",
      },
      {
        header: "Registro",
        accessorKey: "registro_id",
        enableColumnFilter: false,
        
      },
      {
        header: "Usuário",
        accessorKey: "usuario.name",
      },
      {
        header: "Feira",
        accessorFn: (row) => row.feira?.nome || '--',
        id: 'feira_nome',
        enableColumnFilter: true,
        filterFn: 'exact',
        accessorKey: "feira.nome",
      },
      {
        header: "Valor Anterior", 
        accessorFn: (row) => {
          const changes = row.antes;
          if (!changes) return '';

          return Object.entries(changes)
            .filter(([field, values]) => values.antes !== values.depois)
            .map(([field, values]) => {
              const fieldName = translateField(field);
              const value = values.antes === null || values.antes === undefined ? '(vazio)' : values.antes;
              return `${fieldName}: ${value}`;
            })
            .join(' | ');
        },
        id: 'valorAnteriorDetalhe', 
        enableColumnFilter: true, 
        filterFn: 'fuzzy', 
        
       cell: (cell) => {
  const changes = cell.row.original.antes;
  const tabela = cell.row.original.tabela;
  
  if (!changes) return 'N/A';
  
  // Se for a tabela de tags, tratamos o objeto indexado ("0", "1"...)
  if (tabela === 'estande_tags') {
    return Object.values(changes).map((val, idx) => (
      <div key={idx}>
        <small className="text-primary">**Tag Anterior**: {renderTagValue(val.antes)}</small>
      </div>
    ));
  }

  // Lógica padrão para outras tabelas
  const changedValues = Object.entries(changes)
    .filter(([field, values]) => values.antes !== values.depois)
    .map(([field, values]) => (
      <div key={field}> 
        <small>**{translateField(field)}**: {formatValue(values.antes)}</small>
      </div>
    ));

  return changedValues.length > 0 ? changedValues.slice(0, 3) : 'Sem alterações';
},
      },
      {
        header: "Mudanças",
        accessorKey: "antes",
        enableColumnFilter: false,
        cell: (cell) => {
          const changes = cell.row.original.antes;
          const changesCount = countChanges(changes);
          
          return changesCount > 0 ? (
            <Button 
              color="primary" 
              size="sm"
              onClick={() => handleShowDetails(changes)}
            >
              Ver detalhes ({changesCount})
            </Button>
          ) : 'Sem alterações';
        },
      },
      {
        header: "Modificado em",
        accessorKey: "created_at",
        enableColumnFilter: false,
        cell: (cell) => (
          <>
            {handleValidDate(cell.getValue())},
            <small className="text-muted"> {handleValidTime(cell.getValue())}</small>
          </>
        ),
      },
    ],
    []
  );

  const filtrarListaFeiraUsuario = (esperas) => {
    if (['JOTAPIX', 'EVOMAP'].includes(userType)) {
      return esperas;
    } else {
      return esperas.filter(reserva => 
        reserva.feira_id && userFeirasIds.includes(reserva.feira_id)
      );
    }
  };

  const handleValidDate = date => {
    const date1 = moment(new Date(date)).format("DD/MM/YYYY");
    return date1;
  };

  const handleValidTime = (time) => {
    const time1 = new Date(time);
    const getHour = time1.getUTCHours();
    const getMin = time1.getUTCMinutes();
    const getTime = `${getHour}:${getMin}`;
    var meridiem = "";
    if (getHour >= 12) {
      meridiem = "PM";
    } else {
      meridiem = "AM";
    }
    const updateTime = moment(getTime, 'hh:mm').format('hh:mm') + " " + meridiem;
    return updateTime;
  };

  const feirasOptions = useMemo(() => {
    if (!dados || dados.length === 0) return [];
    const feirasUnicas = [...new Set(dados.map(espera => espera.feira?.nome).filter(Boolean))];
    return feirasUnicas.sort().map(nome => ({ 
      value: nome, 
      label: nome 
    }));
  }, [dados]);

  const handleFeiraChange = (feiraValue) => {
    setFeiraSelecionada(feiraValue);
  };

  document.title = "Logs | EVOMAP - Tecnologia que conecta negócios";
  
  return (
    <div className="page-content">
      <ToastContainer closeButton={false} />
      
      <Container fluid>
        <BreadCrumb title="Logs" pageTitle="EVOMAP" />
        <Row>
          <Col lg={12}>
            <Card id="orderList">
              <CardHeader className="border-0">
                <Row className="align-items-center gy-1">
                  <div className="col-sm">
                    <h5 className="card-title mb-0">Logs</h5>
                  </div>
                </Row>
              </CardHeader>
              <CardBody>
                <div>
                  {isLoading ? (
                    <Loader />
                  ) : error ? (
                    <Loader error={error} />
                  ) : dados.length ? (
                    <ConsultaLista
                      columns={columns}
                      data={dados}
                      isGlobalFilter={true}
                      customPageSize={10}
                      tableclassName="align-middle"
                      theadclassName="table-light text-muted"
                      divClassName="table-responsive table-card mb-1"
                      SearchPlaceholder="Pesquise ..."
                      nomeRelatorio="Logs"
                      filters={[]}
                      feirasOptions={feirasOptions}
                      feiraSelecionada={feiraSelecionada}
                      onFeiraChange={handleFeiraChange}
                      hiddenColumns={['registro_id'] }

                    />
                  ) : (
                    <div className="text-center py-5">
                      <i className="ri-database-2-line fs-1 text-muted"></i>
                      <h5 className="mt-3">Nenhum registro encontrado</h5>
                      <p className="text-muted">Não há registros para exibir no momento.</p>
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Modal de Detalhes */}
      <Modal isOpen={modalOpen} toggle={toggleModal} size="lg">
        <ModalHeader toggle={toggleModal}>Detalhes das Alterações</ModalHeader>
        <ModalBody>
  {currentChanges ? (
    <div className="table-responsive">
      <Table striped bordered hover size="sm">
        <thead>
          <tr>
            <th>Campo / Item</th>
            <th>Estado Anterior</th>
            <th>Novo Estado</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(currentChanges).map(([key, values]) => {
            // Se os valores forem objetos complexos (como no log de tags indexado por "0", "1")
            if (values.antes && typeof values.antes === 'object') {
              return (
                <tr key={key}>
                  <td className="font-weight-bold">Item #{key}</td>
                  <td className="text-danger small">{renderTagValue(values.antes)}</td>
                  <td className="text-success small">{renderTagValue(values.depois)}</td>
                </tr>
              );
            }

            // Renderização padrão para campos simples
            if (values.antes === values.depois) return null;
            return (
              <tr key={key}>
                <td className="font-weight-bold">{translateField(key)}</td>
                <td className="text-danger">{formatValue(values.antes)}</td>
                <td className="text-success">{formatValue(values.depois)}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  ) : (
    <p>Nenhuma alteração para mostrar</p>
  )}
</ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleModal}>
            Fechar
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ListaLogs;